import React, { useEffect, useState } from 'react';
import { navigate, withPrefix } from 'gatsby';
import translatePage from '../i18n/translatePage';
import useTranslation from '../hooks/useTranslation';
import styled from 'styled-components';
import variables from '@ingka/variables/design-tokens';
import Loading, { LoadingBall } from '@ingka/loading';
import { useCart } from '../contexts/Cart';
import Form from '../components/Form/Form';
import useUser from '../hooks/useUser';
import { useSubmitOrder } from '../hooks/useOrder';
import SiteMetadata from '../components/SiteMetadata';
import TMarkUp from '../components/TMarkUp';
import ShippingInfoBox from '../components/ShippingInfoBox';
import Breadcrumbs from '../components/Breadcrumbs';
import AdditionalInformationLabel from '../components/AdditionalInformatonLabel';

/* global CSS_CONFIG */

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 30px 0;
  flex-direction: column;
  ${`@media (min-width: ${variables.breakpointM}) {
    flex-direction: row;
    margin: 90px 0;
  }`}
`;

const Box = styled.div`
  width: 100%;
  ${`@media (min-width: ${variables.breakpointM}) {
    width: 50%;
  }`}
`;

const Wrapper = styled.div`
  ${`@media (min-width: ${variables.breakpointM}) {
    max-width: 350px;
  }`}
`;

const CustomerInformation = (): JSX.Element | null => {
  const { user, isLoading } = useUser();
  const { t } = useTranslation();

  const crumbs = [
    { url: withPrefix('/'), text: t('seoContent.page.search') },
    {
      url: '/customer-information',
      text: t('seoContent.page.customerInformation'),
    },
  ];
  const customerInfoLogin = CSS_CONFIG.CUSTOMERINFOLOGIN;
  const title = t('seoContent.page.customerInformation');
  const [{ isLoading: isSubmitLoading, isSuccess }, { onSubmitOrder }] =
    useSubmitOrder();
  const [loginUrl, setLoginUrl] = useState<string>();
  const cartContext = useCart();

  const items = cartContext.getItems();
  const additionalTopLabelText = t('orderForm.extraAdditionalInformationLabel');
  const { additionalInformationLabel } = CSS_CONFIG.FORMSETTINGS;

  useEffect(() => {
    if (window && window.location) {
      const { location } = window;
      const countryAndLanguage = location.pathname.substr(0, 6);
      const newLoginUrl = `${location.origin}${countryAndLanguage}/profile/login/?from=${location}`;

      setLoginUrl(newLoginUrl);
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      navigate('/summary');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!cartContext.state.items.size && !cartContext.state.isLoading) {
      navigate('/');
    }
  }, [cartContext.state]);

  if (!items.length) {
    return null;
  }

  if (isLoading) {
    return (
      <Loading
        text={t('loading', {
          loading: t('orderForm.fieldSet.contactInformation'),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      <FlexContainer>
        <Box>
          <Wrapper>
            <h1>
              <TMarkUp value={t('customerPage.title')} />
            </h1>
            {customerInfoLogin && !user.isAuthenticated && loginUrl && (
              <a
                href={loginUrl}
                style={{
                  display: 'block',
                  paddingTop: '10px',
                  paddingBottom: '20px',
                }}
              >
                <TMarkUp value={t(`customerPage.loginLink`)} />
              </a>
            )}
            {CSS_CONFIG.SHIPPINGINFOBOX && <ShippingInfoBox />}
          </Wrapper>
        </Box>

        <Box>
          {additionalInformationLabel && (
            <AdditionalInformationLabel text={additionalTopLabelText} />
          )}
          <Form
            onSubmit={onSubmitOrder}
            isAuthenticated={user.isAuthenticated}
            values={user.fieldValues}
            isLoading={isSubmitLoading || isSuccess}
          />
        </Box>
      </FlexContainer>
    </>
  );
};
export default translatePage()(CustomerInformation);
